import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, fmtUSD } from "@/lib/api";
import { Target, Edit3, Plus, X, Trash2, Check } from "lucide-react";

const TIMEFRAMES = [
  { v: "day", label: "TODAY" },
  { v: "week", label: "THIS WEEK" },
  { v: "month", label: "THIS MONTH" },
  { v: "year", label: "THIS YEAR" },
  { v: "all", label: "ALL TIME" },
  { v: "custom", label: "ROLLING N DAYS" },
];

const tfLabel = (g) => {
  if (g.timeframe === "custom") return `LAST ${g.custom_days || 30}D`;
  return TIMEFRAMES.find((t) => t.v === g.timeframe)?.label || g.timeframe.toUpperCase();
};

const GoalBar = ({ goal, onEdit }) => {
  const pct = Math.max(0, Math.min(goal.percent, 100));
  const reached = goal.percent >= 100;
  const isAhead = goal.percent >= 50;
  return (
    <div className="p-5 relative group" style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "14px", backdropFilter: "blur(12px)" }} data-testid={`goal-${goal.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: "var(--fg)" }}>
            <Target className="h-3 w-3" strokeWidth={1.5} />
            {goal.label || "Profit goal"} · {tfLabel(goal)}
          </div>
          <div className="flex items-baseline gap-2 mt-1.5">
            <div className="text-2xl font-semibold" style={{ color: goal.progress >= 0 ? "var(--green)" : "var(--loss)", fontFamily: "var(--font-heading)", fontVariantNumeric: "tabular-nums", letterSpacing: "-0.03em" }}>
              {fmtUSD(goal.progress)}
            </div>
            <div className="text-xs" style={{ color: "var(--fg-3)", fontVariantNumeric: "tabular-nums" }}>/ {fmtUSD(goal.amount)}</div>
            {reached && <div className="text-[10px] font-medium ml-2 px-2 py-0.5 rounded-full" style={{ background: "rgba(251,191,36,0.15)", color: "#FBBF24" }}>✓ HIT GOAL</div>}
          </div>
        </div>
        <button
          onClick={() => onEdit(goal)}
          data-testid={`goal-edit-${goal.id}`}
          className="opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ color: "var(--fg-3)" }}
          title="Edit goal"
        >
          <Edit3 className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
      {/* Progress bar — green gradient when tracking, gold when goal is hit. */}
      <div className="relative h-2 overflow-hidden" style={{ background: "rgba(255,255,255,0.04)", borderRadius: "999px" }}>
        <div
          className="absolute left-0 top-0 h-full transition-all duration-500"
          style={{
            width: `${pct}%`,
            background: reached
              ? "linear-gradient(90deg, #FBBF24, #F59E0B)"
              : "linear-gradient(90deg, #22C55E 0%, #3EE07C 50%, #4ADE80 100%)",
            borderRadius: "999px",
            boxShadow: reached ? "none" : "0 0 12px rgba(62, 224, 124, 0.35)",
          }}
        />
      </div>
      <div className="flex items-center justify-between mt-2 text-[10px]" style={{ color: "var(--fg-3)", fontVariantNumeric: "tabular-nums" }}>
        <span>{goal.percent.toFixed(1)}% of target</span>
        <span>{goal.trades} trades in window</span>
      </div>
    </div>
  );
};

const GoalEditor = ({ initial, onSave, onCancel, onDelete }) => {
  const [label, setLabel] = useState(initial?.label || "");
  const [amount, setAmount] = useState(initial?.amount || 1000);
  const [timeframe, setTimeframe] = useState(initial?.timeframe || "month");
  const [customDays, setCustomDays] = useState(initial?.custom_days || 30);
  return (
    <div className="border border-emerald-400/40 bg-zinc-950 p-5 space-y-4" data-testid="goal-editor">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow text-emerald-400 flex items-center gap-1.5">
          <Target className="h-3 w-3" strokeWidth={1.5} /> {initial?.id ? "EDIT GOAL" : "NEW GOAL"}
        </div>
        <button onClick={onCancel} data-testid="goal-cancel" className="text-zinc-500 hover:text-zinc-100">
          <X className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
      <div>
        <div className="label-eyebrow mb-1">LABEL (OPTIONAL)</div>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. Monthly target, Rent money, …"
          data-testid="goal-label"
          className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-400 font-mono-num"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="label-eyebrow mb-1">TARGET ($)</div>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
            data-testid="goal-amount"
            className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-400 font-mono-num"
          />
        </div>
        <div>
          <div className="label-eyebrow mb-1">TIMEFRAME</div>
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            data-testid="goal-timeframe"
            className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-400 font-mono-num"
          >
            {TIMEFRAMES.map((t) => (
              <option key={t.v} value={t.v}>{t.label}</option>
            ))}
          </select>
        </div>
      </div>
      {timeframe === "custom" && (
        <div>
          <div className="label-eyebrow mb-1">ROLLING WINDOW (DAYS)</div>
          <input
            type="number"
            value={customDays}
            min={1}
            max={365}
            onChange={(e) => setCustomDays(parseInt(e.target.value) || 30)}
            data-testid="goal-custom-days"
            className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-400 font-mono-num"
          />
        </div>
      )}
      <div className="flex items-center justify-between pt-2 border-t border-zinc-900">
        {initial?.id ? (
          <button
            onClick={onDelete}
            data-testid="goal-delete"
            className="flex items-center gap-1.5 text-xs text-rose-400 hover:text-rose-300"
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /> REMOVE GOAL
          </button>
        ) : <span />}
        <button
          onClick={() => onSave({ id: initial?.id, label, amount, timeframe, custom_days: customDays, enabled: true })}
          data-testid="goal-save"
          className="flex items-center gap-1.5 bg-emerald-400 text-zinc-950 px-4 py-2 text-xs hover:bg-emerald-300"
        >
          <Check className="h-3.5 w-3.5" strokeWidth={1.5} /> SAVE
        </button>
      </div>
    </div>
  );
};

export default function ProfitGoals() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // goal object being edited, or {new: true}

  const { data } = useQuery({
    queryKey: ["goals"],
    queryFn: async () => (await api.get("/goals")).data,
  });
  const goals = data?.goals || [];

  // Refresh goals when trades change (so progress stays live).
  useEffect(() => {
    qc.invalidateQueries({ queryKey: ["goals"] });
  }, [qc]);

  const saveMut = useMutation({
    mutationFn: async (newList) => (await api.put("/goals", { goals: newList })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["goals"] });
      setEditing(null);
    },
  });

  const onSave = (g) => {
    let next;
    if (g.id && goals.some((x) => x.id === g.id)) {
      next = goals.map((x) => (x.id === g.id ? { ...x, ...g } : x));
    } else {
      next = [...goals, { ...g, id: g.id || `tmp-${Date.now()}` }].slice(0, 2);
    }
    saveMut.mutate(next.map(({ progress, percent, trades, window_start, ...rest }) => rest));
  };
  const onDelete = (id) => {
    const next = goals.filter((x) => x.id !== id);
    saveMut.mutate(next.map(({ progress, percent, trades, window_start, ...rest }) => rest));
  };

  return (
    <div className="space-y-3" data-testid="profit-goals">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-medium" style={{ color: "var(--accent)" }}>
          <Target className="h-3 w-3" strokeWidth={1.5} /> PROFIT GOALS
        </div>
        {goals.length < 2 && !editing && (
          <button
            onClick={() => setEditing({ new: true, amount: 1000, timeframe: "month" })}
            data-testid="goal-add-btn"
            className="flex items-center gap-1.5 text-xs font-medium transition-colors"
            style={{ color: "var(--accent)" }}
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
            {goals.length === 0 ? "Set your first goal" : "Add second goal"}
          </button>
        )}
      </div>

      {goals.length === 0 && !editing && (
        <div className="border border-dashed border-zinc-800 p-6 text-center text-xs text-zinc-500 font-mono-num">
          No goals set. Add a daily, weekly, monthly, yearly, or custom-window target to track your progress with a live green bar.
        </div>
      )}

      {goals.length > 0 && (
        <div className={`grid gap-3 ${goals.length === 2 ? "lg:grid-cols-2" : ""}`}>
          {goals.map((g) => (
            <GoalBar key={g.id} goal={g} onEdit={setEditing} />
          ))}
        </div>
      )}

      {editing && (
        <GoalEditor
          initial={editing.new ? null : editing}
          onSave={onSave}
          onCancel={() => setEditing(null)}
          onDelete={() => editing?.id && onDelete(editing.id)}
        />
      )}
    </div>
  );
}
